import React, { useCallback, useEffect, useRef } from 'react';
import { styled } from '@compiled/react';
import Field from '@atlaskit/form/Field';
import Form from '@atlaskit/form/Form';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import messages from '../../common/messages';
// eslint-disable-next-line jira/import/no-parent-imports
import Buttons from '../form/form-controls/buttons';
import type { EditViewProps } from './types';

function EditView<FieldValue>(props: EditViewProps<FieldValue>) {
	const {
		confirmButtonLabel,
		cancelButtonLabel,
		defaultValue,
		onDoNotFocusOnEditButton,
		editView,
		hideActionButtons,
		isRequired,
		keepEditViewOpenOnBlur,
		onCancel,
		onConfirm,
		validate,
	} = props;

	const { formatMessage } = useIntl();
	const editViewRef = useRef<HTMLElement>(null);
	const wasFocusReceivedSinceLastBlurRef = useRef(false);
	const timerRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		editViewRef.current?.focus();
	}, [editViewRef]);

	const tryAutoSubmitWhenBlur = useCallback(
		(
			isFormInvalid: boolean,
			onSubmit: (e?: React.FormEvent<HTMLFormElement>) => void,
			formRef: React.RefObject<HTMLFormElement>,
		) => {
			if (!isFormInvalid && !wasFocusReceivedSinceLastBlurRef.current && formRef.current) {
				onDoNotFocusOnEditButton();
				if (formRef.current.checkValidity()) {
					onSubmit();
				}
			}
		},
		[onDoNotFocusOnEditButton],
	);

	/** If keepEditViewOpenOnBlur prop is set to false, will call confirmIfUnfocused() which
	 *  confirms the value, if the focus is not transferred to the action buttons
	 *
	 *  When you're in `editing` state, the focus will be on the input field. And if you use keyboard
	 *  to navigate to `submit` button, this function will be invoked. Then function `onEditViewWrapperFocus`
	 *  will be called, the timeout used here is making sure `onEditViewWrapperFocus` is always called before
	 *  `autoSubmitWhenBlur`.
	 *
	 *  There are two paths here the function can be triggered:
	 *
	 *  - focus on input first, and then use keyboard to `submit`
	 *  - focus on input first, and then click anywhere else on the page (outside of edit view wrapper) to `submit` (auto save)
	 */
	const onEditViewWrapperBlur = useCallback(
		(
			isFormInvalid: boolean,
			onSubmit: (e?: React.FormEvent<HTMLFormElement>) => void,
			formRef: React.RefObject<HTMLFormElement>,
		) => {
			if (!keepEditViewOpenOnBlur) {
				wasFocusReceivedSinceLastBlurRef.current = false;
				timerRef.current = setTimeout(
					() => tryAutoSubmitWhenBlur(isFormInvalid, onSubmit, formRef),
					0,
				);
			}
		},
		[keepEditViewOpenOnBlur, tryAutoSubmitWhenBlur],
	);

	/** Gets called when focus is transferred to the editView, or action buttons
	 *
	 * There are three paths here the function can be called:
	 *
	 * - when a user click the `editView`
	 * - when a user use keyboard to tab into `editView`
	 * - when a user use keyboard to tab into `submit` when they were on input field
	 */
	const onEditViewWrapperFocus = useCallback(() => {
		wasFocusReceivedSinceLastBlurRef.current = true;
	}, []);

	const onCancelClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			event.preventDefault();
			onCancel();
		},
		[onCancel],
	);

	return (
		<Form onSubmit={(data: { inlineEdit: FieldValue }) => onConfirm(data.inlineEdit)}>
			{({ formProps: { onKeyDown, onSubmit, ref: formRef } }) => (
				<form
					/**
					 * It is not normally acceptable to add key handlers to non-interactive elements
					 * as this is an accessibility anti-pattern. However, because this instance is
					 * to add support for keyboard functionality instead of creating an inaccessible
					 * custom element, we can add role="presentation" so that there is no negative
					 * impacts to assistive technologies.
					 */
					role="presentation"
					onKeyDown={(e) => {
						onKeyDown(e);
						if (e.key === 'Esc' || e.key === 'Escape') {
							onCancel();
						}
					}}
					onSubmit={onSubmit}
					ref={formRef}
				>
					<Field
						name="inlineEdit"
						defaultValue={defaultValue}
						validate={validate}
						isRequired={isRequired}
						key="edit-view" // used for reset to default value
					>
						{({ fieldProps, error }) => (
							<EditViewWrapper
								onBlur={() => {
									onEditViewWrapperBlur(fieldProps.isInvalid, onSubmit, formRef);
								}}
								onFocus={onEditViewWrapperFocus}
							>
								{editView(
									{
										...fieldProps,
										errorMessage: error,
									},
									editViewRef,
								)}
								{!hideActionButtons ? (
									<Buttons
										cancelButtonLabel={
											cancelButtonLabel ?? formatMessage(messages.cancelButtonLabel)
										}
										confirmButtonLabel={
											confirmButtonLabel ?? formatMessage(messages.confirmButtonLabel)
										}
										onMouseDown={onDoNotFocusOnEditButton}
										onCancelClick={onCancelClick}
									/>
								) : (
									/** This is to allow Ctrl + Enter to submit without action buttons */
									<button hidden type="submit" aria-label="Submit" />
								)}
							</EditViewWrapper>
						)}
					</Field>
				</form>
			)}
		</Form>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditViewWrapper = styled.div({
	marginTop: `${token('space.negative.100', '-8px')}`,
	maxWidth: '100%',
	position: 'relative',
});

export default EditView;
